const { getInsertProperties, queryPaginationTransform } = require('../../tools/sql-tools')
const { nanoid } = require('nanoid')

/**
 * @typedef {Object} PriceEntry
 * @property {string} productId
 * @property {number} sellPrice
 * @property {string} effectiveDate
*/

/**
 * @typedef {Object} Service
 * @property {string} id
 * @property {string} fsid
*/

class LocationPrices {
  constructor (pgConnection) {
    this.pgConnection = pgConnection
    this.LIMIT = 25
  }

  async getProgramProducts (programIds, isWarehouseUser) {
    if (!programIds || !programIds.length) {
      return []
    }

    const { placeholders, values } = programIds.reduce((acc, id, index) => {
      acc.placeholders += `d.id ilike $${index + 1}`
      if (index < programIds.length - 1) {
        acc.placeholders += ' or '
      }
      acc.values.push(`%configuration:${id}:service%`)
      return acc
    }, {placeholders: '', values: []})

    const warehouseClause = isWarehouseUser ? ` where p.service ilike '%warehouse%' ` : ` where p.service not ilike '%warehouse%' `
    const { rows: [row] } = await this.pgConnection.query(`
      SELECT
        coalesce(
          (
            select jsonb_agg(p)
            from (
              with product_per_service as (
                select
                  d.doc ->> 'service' as service,
                  p.product
                from couchdb.db_integrated_data d
                cross join lateral jsonb_array_elements(coalesce(d.doc->'products', '[]'::jsonb)) as p(product)
                where (${placeholders})
              )
              select
                product as "productId",
                coalesce(jsonb_agg(json_build_object('id', s.id, 'fsid', p.service)), '[]'::jsonb) as services
              from product_per_service p
              left join avocado.data_service s on p.service = s.fsid 
              ${warehouseClause}
              group by 1
            ) p
          ), '[]'::jsonb) as products
    `, values)
    return row.products
  }

  /**
   * @param {string} locationFsid
   * @param {string} productId
   * @param {string} userFsid
   * @param {number} page
   * @param {string} service
  */
  async getProductSellPrices (locationFsid, productId, userFsid, page, service) {
    const sku = productId.replace('product:', '')
    const params = [locationFsid, sku]
    let serviceFilter = ''
    if (service) {
      serviceFilter = ' and s.fsid = $3 '
      params.push(service)
    }
    const paginatedQuery = queryPaginationTransform({
      baseQuery: `
        with base_prices as (
          select
            l.fsid as location_fsid,
            l.name_of_pharmacy,
            ph.*,
           row_number() over (partition by sku, location_id order by date) as price_order
          from avocado.data_location l
          left join real.price_history ph on ph.location_id = l.uuid
          left join avocado.data_service s on s.id = ph.service_id
          where l.fsid = $1 and ph.sku = $2 ${serviceFilter}
        ),
        sell_prices as (
          select
            tp.id,
            tp.date as "effectiveDate",
            '${userFsid}' = tp.updated_by as "updatedByCurrentUser",
            concat('product:', tp.sku) as "productId",
            coalesce(u."name", regexp_replace(tp.updated_by, 'org.couchdb.user:', '' )) as "updatedBy",
            json_build_object(
              'fsid', tp.location_fsid,
              'fullName', tp.name_of_pharmacy
            ) as "location",
            json_build_object(
              'from', fp.sellprice,
              'to', tp.sellprice 
            ) as "sellPrice",
            json_build_object(
              'id', s.id,
              'fsid', s.fsid,
              'name', s."name"
            ) as "service",
            tp.created_at as "createdAt",
            tp.updated_at as "updatedAt"
          from base_prices tp
          left join base_prices fp on
            tp.sku = fp.sku and 
            tp.location_id = fp.location_id and
            tp.price_order = fp.price_order + 1
          left join avocado.data_service s on s.id = tp.service_id
          left join avocado.data_user u on u.fsid = tp.updated_by 
        )
      `,
      cteName: 'sell_prices',
      orderByClause: 'order by "effectiveDate" desc',
      pageNo: page,
      maxLimit: this.LIMIT
    })

    const { rows: [data] } = await this.pgConnection.query(paginatedQuery, params)
    return data
  }

  /**
   * @param {string} locationFsid
   * @param {string} userFsid
   * @param {PriceEntry[]} entry
   * @param {string | null} organisationId
  */
  async uploadSellPrices (locationFsid, userFsid, entry, organisationId = null) {
    const error = new Error()
    let startIndex = 2
    const {args, params} = entry.reduce((acc, curr, index) => {
      acc.args += `(ph.sku = $${index + startIndex} and ph.date = $${index + startIndex + 1})`
      if (index < entry.length - 1) {
        acc.args += ' or '
      }
      acc.params.push(curr.productId.replace('product:', ''), curr.effectiveDate)
      startIndex += 1
      return acc
    }, { args: '', params: [locationFsid] })

    const { rows: [existing] } = await this.pgConnection.query(`
      with base_location as (
        select
          uuid
        from avocado.data_location
        where fsid = $1
      )
      select
        bl.uuid as location_id,
        coalesce(
          (
            select jsonb_agg(p)
            from (
              select
                concat('product:', ph.sku) as "productId",
                ph.date as "effectiveDate",
                s.fsid as "serviceFsid"
              from real.price_history ph
              left join avocado.data_service s on s.id = ph.service_id
              where ph.location_id = bl.uuid and (${args})
              group by 1, 2, 3
            ) p
          ),
          '[]'::jsonb
        ) as "sellPrices"
      from base_location bl
    `, params)

    if (!existing) {
      error.message = 'User not tied to a location'
      error.status = 401
      throw error
    }

    if (existing.sellPrices.length > 0) {
      error.message = 'Sell price entries already exist for provided products effective dates'
      error.status = 409
      error.errors = existing.sellPrices
      throw error
    }

    const validOrganisationId = await this.resolveOrganisationId(organisationId)
    const txnRef = nanoid(10)
    const parsedRows = []
    for (const price of entry) {
      const sku = price.productId.replace('product:', '')
      parsedRows.push({
        location_id: existing.location_id,
        sku,
        sellprice: price.sellPrice,
        date: price.effectiveDate,
        txn_ref: txnRef,
        updated_by: userFsid,
        service_id: null,
        organisation_id: validOrganisationId
      })
    }

    const { columns, values, placeholders } = getInsertProperties(parsedRows)
    const { rows } = await this.pgConnection.query(`
      insert into real.price_history (${columns})
      values ${placeholders}
      on conflict (sku, date, location_id)
      WHERE location_id IS NOT null AND service_id IS NULL
      do nothing
      returning *
    `, values)
    return rows
  }

  async resolveOrganisationId (organisationId) {
    if (!organisationId || !this.isUuid(organisationId)) {
      return null
    }

    const { rows } = await this.pgConnection.query(`
      select id
      from avocado.data_organisation
      where id = $1
      limit 1
    `, [organisationId])

    return rows[0] ? rows[0].id : null
  }

  isUuid (value) {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
  }

  async getSellPriceList (locationFsid, date) {
    const params = [locationFsid]
    let dateFilter = ''
    if (!date) {
      dateFilter = ' and date <= CURRENT_DATE '
    } else {
      dateFilter = ` and date <= $2::timestamptz `
      params.push(date)
    }

    const { rows: [priceList] } = await this.pgConnection.query(`
      with base_prices as (
        select
          ph.*,
          row_number() over (partition by sku, location_id order by date desc) as price_order
        from avocado.data_location l
        left join real.price_history ph on l.uuid = ph.location_id 
        where l.fsid = $1 ${dateFilter}
      )
      select
        $1 as "locationFsid",
        ${date ? '$2::timestamptz' : 'CURRENT_DATE'} as "referenceDate",
        coalesce(
          (
            select jsonb_agg(p)
            from (
              select
                id,
                concat('product:', sku) as "productId",
                sellprice::float as "sellPrice",
                date as "effectiveDate"
              from base_prices
              where price_order = 1
            ) p
          ),
          '[]'::jsonb
        ) as "sellPrices"
    `, params)
    return priceList
  }

  /**
   * @param {Object} params
   * @param {string} params.locationFsid
   * @param {string} params.productId
   * @param {number} params.page
   * @param {number} params.maxLimit
  */
  async getProductPurchasePrices ({locationFsid, productId, page, maxLimit}) {
    const sku = productId.replace('product:', '')
    const params = [locationFsid, sku]
    const paginatedQuery = queryPaginationTransform({
      baseQuery: `
        with inbound_shipments as (
          select
            sl.id as shipment_line_id,
            s.shipment_id,
            coalesce(sl.destination_sku, sl.sku) as sku,
            s.status,
            l.name_of_pharmacy,
            l.fsid,
            ph.date,
            ph.service_id,
            sl.price_history_id,
            avocado.round_half_even(ph.sellprice , 2)::float as sellprice,
            coalesce(o.created_at, sp.created_at) as reference_date,
            row_number() over (partition by sl.destination_sku order by coalesce(o.created_at, sp.created_at) desc) as price_order,
            ph.updated_by,
            coalesce(sp.created_at, s.created_at) as created_at,
            coalesce(sp.updated_at, s.updated_at) as updated_at
          from avocado.data_shipment s
          left join avocado.data_order o on o.order_id = s.order_id and o.destination_id = s.destination_id
          left join avocado.data_shipmentsnapshot sp on sp.shipment_id = s.shipment_id and sp.status = 'new'
          left join avocado.data_shipmentline sl on s.id = sl.shipment_id
          -- Only return shipments whose purchase price values can be linked to
          right join real.price_history ph on ph.id = sl.price_history_id
          left join avocado.data_location l on l.fsid = s.origin_id
          where s.destination_id = $1 and coalesce(sl.destination_sku, sl.sku) = $2
        ),
        price_list as (
          select
            coalesce(tp.price_history_id, tp.shipment_line_id) as id,
            tp.date as "effectiveDate",
            null as "updatedByCurrentUser",
            concat('product:', tp.sku) as "productId",
            coalesce(u."name", regexp_replace(tp.updated_by, 'org.couchdb.user:', '' )) as "updatedBy",
            json_build_object(
              'fsid', tp.fsid,
              'fullName', tp.name_of_pharmacy
            ) as "location",
            json_build_object(
              'from', fp.sellprice,
              'to', tp.sellprice
            ) as "purchasePrice",
            json_build_object(
              'id', s.id,
              'fsid', s.fsid,
              'name', s."name"
            ) as "service",
            json_build_object(
              'shipmentId', tp.shipment_id,
              'referenceDate', tp.reference_date,
              'status', tp.status 
            ) as "shipment",
            tp.created_at as "createdAt",
            tp.updated_at as "updatedAt",
            tp.price_order as "priceOrder"
          from inbound_shipments tp
          left join inbound_shipments fp on
            tp.sku = fp.sku and 
            tp.price_order = fp.price_order - 1
          left join avocado.data_service s on s.id = tp.service_id
          left join avocado.data_user u on u.fsid = tp.updated_by
        )
      `,
      cteName: 'price_list',
      orderByClause: 'order by "priceOrder" asc',
      pageNo: page,
      maxLimit: maxLimit || this.LIMIT
    })

    const { rows: [data] } = await this.pgConnection.query(paginatedQuery, params)
    return data
  }

  /**
   * @param {string} locationFsid
   * @param {string} productId
  */
  async getLatestProductPurchasePrice (locationFsid, productId) {
    const {data} = await this.getProductPurchasePrices({ locationFsid, productId, maxLimit: 1 })
    return data[0] || {}
  }
}

module.exports = { LocationPrices }
